import React, { useRef, useEffect, useState, useCallback } from 'react';
import * as THREE from 'three';
import { STLLoader, OBJLoader, PLYLoader, STLExporter } from 'three-stdlib';
import { 
  RotateCcw, Move, ZoomIn, Magnet, Ruler, Box, Grid3X3, 
  Maximize2, RefreshCw, Camera, Maximize, Hand, Rotate3d,
  ChevronUp, ChevronDown, ChevronLeft, ChevronRight, Expand,
  Save
} from 'lucide-react';

// Calculate actual mesh volume using signed volume of tetrahedra
const calculateMeshVolume = (geometry) => {
  const positions = geometry.attributes.position.array;
  let volume = 0;
  
  // Calculate volume using divergence theorem
  // For each triangle, calculate signed volume of tetrahedron from origin
  for (let i = 0; i < positions.length; i += 9) {
    const v1 = new THREE.Vector3(positions[i], positions[i+1], positions[i+2]);
    const v2 = new THREE.Vector3(positions[i+3], positions[i+4], positions[i+5]);
    const v3 = new THREE.Vector3(positions[i+6], positions[i+7], positions[i+8]);
    
    // Signed volume of tetrahedron = (v1 · (v2 × v3)) / 6
    const cross = new THREE.Vector3().crossVectors(v2, v3);
    volume += v1.dot(cross);
  }
  
  return Math.abs(volume / 6);
};

// Calculate actual mesh surface area
const calculateMeshSurfaceArea = (geometry) => {
  const positions = geometry.attributes.position.array;
  let area = 0;
  
  // Calculate area of each triangle
  for (let i = 0; i < positions.length; i += 9) {
    const v1 = new THREE.Vector3(positions[i], positions[i+1], positions[i+2]);
    const v2 = new THREE.Vector3(positions[i+3], positions[i+4], positions[i+5]);
    const v3 = new THREE.Vector3(positions[i+6], positions[i+7], positions[i+8]);
    
    // Area = 0.5 * ||(v2-v1) × (v3-v1)||
    const edge1 = new THREE.Vector3().subVectors(v2, v1);
    const edge2 = new THREE.Vector3().subVectors(v3, v1);
    const cross = new THREE.Vector3().crossVectors(edge1, edge2);
    area += cross.length() * 0.5;
  }
  
  return area;
};

// Server conversion utility with extended timeout for STEP files
const convertFileToSTL = async (file, fileType) => {
  const formData = new FormData();
  formData.append('file', file);
  
  const API_BASE = process.env.REACT_APP_BACKEND_URL || '';
  const endpoint = fileType === '3mf' ? '/api/convert/3mf-to-stl' : '/api/convert/step-to-stl';
  
  // Create an AbortController with extended timeout for STEP files
  // 180 seconds (3 minutes) for STEP to handle large files in production
  const controller = new AbortController();
  const timeoutId = setTimeout(() => controller.abort(), fileType === 'step' ? 180000 : 60000);
  
  try {
    const response = await fetch(`${API_BASE}${endpoint}`, {
      method: 'POST',
      body: formData,
      signal: controller.signal
    });
    
    clearTimeout(timeoutId);
    
    if (!response.ok) {
      const errorText = await response.text().catch(() => response.statusText);
      throw new Error(`Server conversion failed: ${errorText || response.statusText}`);
    }
    
    const result = await response.json();
    
    if (!result.success) {
      throw new Error(result.error || 'Conversion failed on server');
    }
    
    // Convert hex STL data back to binary
    const stlHex = result.stl_data;
    const stlBuffer = new ArrayBuffer(stlHex.length / 2);
    const stlView = new Uint8Array(stlBuffer);
    
    for (let i = 0; i < stlHex.length; i += 2) {
      stlView[i / 2] = parseInt(stlHex.substring(i, i + 2), 16);
    }
    
    // Parse converted STL
    const stlLoader = new STLLoader();
    const geometry = stlLoader.parse(stlBuffer);
    
    // Return geometry along with detected units info
    return {
      geometry,
      detectedUnits: result.detected_units || 'unknown',
      unitsDetectionMessage: result.units_detection_message || ''
    };
  } catch (error) {
    clearTimeout(timeoutId);
    if (error.name === 'AbortError') {
      throw new Error(`${fileType.toUpperCase()} conversion timed out. Please try with a smaller file or contact support.`);
    }
    throw error;
  }
};

// Tool definitions
const TOOLS = {
  ROTATE: 'rotate',
  PAN: 'pan',
  ZOOM: 'zoom',
  SNAP: 'snap',
  SCALE: 'scale',
  MEASURE: 'measure' // Hidden for now
};

const TOOL_HINTS = {
  [TOOLS.ROTATE]: 'Rotate Mode - Click & drag to orbit around the model',
  [TOOLS.PAN]: 'Pan Mode - Click & drag to move the view',
  [TOOLS.ZOOM]: 'Zoom Mode - Click & drag or scroll to zoom',
  [TOOLS.SNAP]: 'Snap Mode - Click a face to snap it to the build plate',
  [TOOLS.SCALE]: 'Scale Mode - Adjust model size',
  [TOOLS.MEASURE]: 'Measure Mode - Click two points to measure distance'
};

// Normalize angle to range [-180, 180]
const normalizeAngle = (degrees) => {
  let normalized = degrees % 360;
  if (normalized > 180) normalized -= 360;
  if (normalized < -180) normalized += 360;
  return normalized;
};

const ThreeDPreview = ({ file, cadFileUrl, onAnalysis, onFileModified, className = '', material, strength, quality, units = 'mm' }) => {
  const canvasRef = useRef(null);
  const containerRef = useRef(null);
  const meshRef = useRef(null);
  const sceneRef = useRef(null);
  const cameraRef = useRef(null);
  const rendererRef = useRef(null);
  const raycasterRef = useRef(new THREE.Raycaster());
  const highlightMeshRef = useRef(null); // For face highlighting on hover
  const controlsRef = useRef({
    isDragging: false,
    previousMousePosition: { x: 0, y: 0 },
    cameraDistance: 5,
    cameraRotation: { theta: Math.PI / 4, phi: Math.PI / 4 },
    panOffset: { x: 0, y: 0, z: 0 } // Track pan offset
  });
  
  // Measurement state
  const [measurePoints, setMeasurePoints] = useState([]);
  const [measureDistance, setMeasureDistance] = useState(null);
  const measureMarkersRef = useRef([]);
  const measureLineRef = useRef(null);
  const measureClickCountRef = useRef(0); // Track click count directly
  
  const [previewState, setPreviewState] = useState('idle');
  const [errorMessage, setErrorMessage] = useState('');
  const [fileInfo, setFileInfo] = useState(null);
  const [isStepFile, setIsStepFile] = useState(false);
  
  // New toolbar state
  const [activeTool, setActiveTool] = useState(TOOLS.ROTATE);
  const activeToolRef = useRef(TOOLS.ROTATE); // Ref to track current tool for event handlers
  const [showWireframe, setShowWireframe] = useState(false);
  const [showGrid, setShowGrid] = useState(true);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showRotatePanel, setShowRotatePanel] = useState(false);
  const [showScalePanel, setShowScalePanel] = useState(false);
  const [modelRotation, setModelRotation] = useState({ x: 0, y: 0, z: 0 });
  const modelRotationRef = useRef({ x: 0, y: 0, z: 0 }); // Ref to track current rotation for snap
  const [modelScale, setModelScale] = useState(100); // Percentage scale (100 = original size)
  const originalScaleRef = useRef(1); // Store the original scale factor
  const gridHelperRef = useRef(null);
  const skipRotationEffectRef = useRef(false); // Flag to skip rotation effect during snap
  
  // Orientation indicator (axis gizmo) refs
  const axisCanvasRef = useRef(null);
  const axisSceneRef = useRef(null);
  const axisCameraRef = useRef(null);
  const axisRendererRef = useRef(null);
  
  // Viewport container ref for resize observer
  const viewportRef = useRef(null);
  
  // Track unsaved model changes
  const [hasUnsavedChanges, setHasUnsavedChanges] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const originalFileRef = useRef(null); // Store reference to original file
  const initialRotationRef = useRef({ x: 0, y: 0, z: 0 });
  const initialScaleRef = useRef(100);

  // Keep modelRotationRef in sync with modelRotation state
  useEffect(() => {
    modelRotationRef.current = modelRotation;
  }, [modelRotation]);

  // Keep activeToolRef in sync with activeTool state
  useEffect(() => {
    activeToolRef.current = activeTool;
    // Auto-collapse panels when switching tools
    setShowRotatePanel(false);
    setShowScalePanel(false);
    // Update cursor based on active tool
    if (canvasRef.current) {
      canvasRef.current.style.cursor = (activeTool === TOOLS.SNAP || activeTool === TOOLS.MEASURE) ? 'crosshair' : 'grab';
    }
    // Clear face highlight when leaving snap mode
    if (activeTool !== TOOLS.SNAP && highlightMeshRef.current && sceneRef.current) {
      sceneRef.current.remove(highlightMeshRef.current);
      highlightMeshRef.current.geometry.dispose();
      highlightMeshRef.current.material.dispose();
      highlightMeshRef.current = null;
    }
  }, [activeTool]);

  // Apply model rotation when it changes (unless skipped by snap operation)
  useEffect(() => {
    if (meshRef.current && !skipRotationEffectRef.current) {
      console.log('useEffect START - modelRotation:', modelRotation);
      
      // Set rotation using Euler directly (instead of quaternion)
      meshRef.current.rotation.set(
        THREE.MathUtils.degToRad(modelRotation.x),
        THREE.MathUtils.degToRad(modelRotation.y),
        THREE.MathUtils.degToRad(modelRotation.z)
      );
      
      // Force update the mesh's matrix
      meshRef.current.updateMatrix();
      meshRef.current.updateMatrixWorld(true);
      
      // Also update the scene if available
      if (sceneRef.current) {
        sceneRef.current.updateMatrixWorld(true);
      }
      
      console.log('useEffect END - mesh.rotation:', {
        x: THREE.MathUtils.radToDeg(meshRef.current.rotation.x),
        y: THREE.MathUtils.radToDeg(meshRef.current.rotation.y),
        z: THREE.MathUtils.radToDeg(meshRef.current.rotation.z)
      });
      console.log('useEffect END - mesh.quaternion:', meshRef.current.quaternion.toArray());
    } else {
      console.log('useEffect SKIPPED - skipRotationEffectRef was true');
    }
    // Reset the skip flag after the effect runs
    skipRotationEffectRef.current = false;
  }, [modelRotation]);

  // Apply model scale when it changes
  useEffect(() => {
    if (meshRef.current && originalScaleRef.current) {
      const scaleFactor = (modelScale / 100) * originalScaleRef.current;
      meshRef.current.scale.set(scaleFactor, scaleFactor, scaleFactor);
      
      // Reposition to sit on grid after scaling
      meshRef.current.geometry.computeBoundingBox();
      const box = new THREE.Box3().setFromObject(meshRef.current);
      meshRef.current.position.y = -box.min.y;
    }
  }, [modelScale]);

  // Initialize axis indicator when preview is ready and canvas is available
  useEffect(() => {
    if (previewState !== 'success' || !axisCanvasRef.current || axisRendererRef.current) return;
    
    const axisCanvas = axisCanvasRef.current;
    
    // Create a separate scene for the axis indicator
    const axisScene = new THREE.Scene();
    axisScene.background = null; // Transparent background
    
    // Create orthographic camera for the axis view
    const axisSize = 1;
    const axisCamera = new THREE.OrthographicCamera(-axisSize, axisSize, axisSize, -axisSize, 0.1, 10);
    axisCamera.position.set(0, 0, 2);
    axisCamera.lookAt(0, 0, 0);
    
    // Create professional axis arrows
    const axisLength = 0.6;
    const arrowHeadLength = 0.15;
    const arrowHeadWidth = 0.08;
    
    // X axis (Red)
    const xDir = new THREE.Vector3(1, 0, 0);
    const xArrow = new THREE.ArrowHelper(xDir, new THREE.Vector3(0, 0, 0), axisLength, 0xef4444, arrowHeadLength, arrowHeadWidth);
    axisScene.add(xArrow);
    
    // Y axis (Green)
    const yDir = new THREE.Vector3(0, 1, 0);
    const yArrow = new THREE.ArrowHelper(yDir, new THREE.Vector3(0, 0, 0), axisLength, 0x22c55e, arrowHeadLength, arrowHeadWidth);
    axisScene.add(yArrow);
    
    // Z axis (Blue)
    const zDir = new THREE.Vector3(0, 0, 1);
    const zArrow = new THREE.ArrowHelper(zDir, new THREE.Vector3(0, 0, 0), axisLength, 0x3b82f6, arrowHeadLength, arrowHeadWidth);
    axisScene.add(zArrow);
    
    // Add axis labels using sprites
    const createAxisLabel = (text, color, position) => {
      const labelCanvas = document.createElement('canvas');
      labelCanvas.width = 64;
      labelCanvas.height = 64;
      const ctx = labelCanvas.getContext('2d');
      ctx.fillStyle = color;
      ctx.font = 'bold 48px Arial';
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      ctx.fillText(text, 32, 32);
      
      const texture = new THREE.CanvasTexture(labelCanvas);
      const material = new THREE.SpriteMaterial({ map: texture, transparent: true });
      const sprite = new THREE.Sprite(material);
      sprite.position.copy(position);
      sprite.scale.set(0.25, 0.25, 1);
      return sprite;
    };
    
    axisScene.add(createAxisLabel('X', '#ef4444', new THREE.Vector3(0.85, 0, 0)));
    axisScene.add(createAxisLabel('Y', '#22c55e', new THREE.Vector3(0, 0.85, 0)));
    axisScene.add(createAxisLabel('Z', '#3b82f6', new THREE.Vector3(0, 0, 0.85)));
    
    // Create renderer for axis indicator
    const axisRenderer = new THREE.WebGLRenderer({ 
      canvas: axisCanvas, 
      alpha: true, 
      antialias: true 
    });
    axisRenderer.setSize(80, 80);
    axisRenderer.setPixelRatio(window.devicePixelRatio);
    axisRenderer.setClearColor(0x000000, 0); // Transparent
    
    // Store refs
    axisSceneRef.current = axisScene;
    axisCameraRef.current = axisCamera;
    axisRendererRef.current = axisRenderer;
    
    // Initial render
    axisRenderer.render(axisScene, axisCamera);
    
    return () => {
      if (axisRendererRef.current) {
        axisRendererRef.current.dispose();
        axisRendererRef.current = null;
      }
    };
  }, [previewState]);

  // Model rotation handlers
  const handleRotate90 = (axis, direction) => {
    setModelRotation(prev => ({
      ...prev,
      [axis]: (prev[axis] + (direction * 90)) % 360
    }));
  };

  const handleSliderChange = (axis, value) => {
    setModelRotation(prev => ({
      ...prev,
      [axis]: parseFloat(value)
    }));
  };

  const handleResetModelRotation = () => {
    setModelRotation({ x: 0, y: 0, z: 0 });
  };

  // Scale handlers
  const handleScaleChange = (value) => {
    const newScale = Math.max(10, Math.min(500, parseFloat(value) || 100));
    setModelScale(newScale);
  };

  const handleScalePreset = (preset) => {
    setModelScale(preset);
  };

  const handleResetScale = () => {
    setModelScale(100);
  };

  // Track changes to rotation and scale
  useEffect(() => {
    const rotationChanged = 
      modelRotation.x !== initialRotationRef.current.x ||
      modelRotation.y !== initialRotationRef.current.y ||
      modelRotation.z !== initialRotationRef.current.z;
    const scaleChanged = modelScale !== initialScaleRef.current;
    
    setHasUnsavedChanges(rotationChanged || scaleChanged);
  }, [modelRotation, modelScale]);

  // Save modified model
  const saveModifiedModel = useCallback(async () => {
    if (!meshRef.current || !originalFileRef.current) {
      console.error('No mesh or original file to save');
      return;
    }
    
    setIsSaving(true);
    
    try {
      // Clone the geometry and apply transformations
      const geometry = meshRef.current.geometry.clone();
      
      // Apply the mesh's current transformation matrix to the geometry
      geometry.applyMatrix4(meshRef.current.matrix);
      
      // Create a temporary mesh for export
      const exportMesh = new THREE.Mesh(geometry, meshRef.current.material);
      
      // Export as STL (binary for smaller file size)
      const exporter = new STLExporter();
      const stlData = exporter.parse(exportMesh, { binary: true });
      
      // Create blob and file
      const blob = new Blob([stlData], { type: 'application/octet-stream' });
      
      // Generate new filename
      const originalName = originalFileRef.current.name;
      const baseName = originalName.replace(/\.[^/.]+$/, ''); // Remove extension
      const newFileName = `${baseName}_modified.stl`;
      
      // Create a new File object
      const modifiedFile = new File([blob], newFileName, { type: 'application/octet-stream' });
      
      // Clean up
      geometry.dispose();
      
      console.log('Model saved:', newFileName, 'Size:', (blob.size / 1024).toFixed(2), 'KB');
      
      // Update initial values to current (so button unhighlights)
      initialRotationRef.current = { ...modelRotation };
      initialScaleRef.current = modelScale;
      setHasUnsavedChanges(false);
      
      // Call onFileModified callback with the new file to update the quote
      if (onFileModified) {
        onFileModified(modifiedFile);
      }
      
      // Show success feedback
      alert(`Model saved as "${newFileName}"\n\nThe quote will be recalculated with your changes.`);
      
    } catch (error) {
      console.error('Error saving model:', error);
      alert('Failed to save model. Please try again.');
    } finally {
      setIsSaving(false);
    }
  }, [modelRotation, modelScale, onFileModified]);

  // Toggle wireframe mode
  const toggleWireframe = useCallback(() => {
    if (meshRef.current) {
      const newWireframe = !showWireframe;
      setShowWireframe(newWireframe);
      meshRef.current.material.wireframe = newWireframe;
    }
  }, [showWireframe]);

  // Toggle grid visibility
  const toggleGrid = useCallback(() => {
    if (gridHelperRef.current) {
      const newShowGrid = !showGrid;
      setShowGrid(newShowGrid);
      gridHelperRef.current.visible = newShowGrid;
    }
  }, [showGrid]);

  // Fit model to view
  const fitToView = useCallback(() => {
    if (meshRef.current && cameraRef.current) {
      const box = new THREE.Box3().setFromObject(meshRef.current);
      const center = box.getCenter(new THREE.Vector3());
      const size = box.getSize(new THREE.Vector3());
      const maxDim = Math.max(size.x, size.y, size.z);
      const fov = cameraRef.current.fov * (Math.PI / 180);
      let cameraZ = Math.abs(maxDim / 2 / Math.tan(fov / 2));
      cameraZ *= 1.5; // Add some padding
      
      controlsRef.current.cameraDistance = cameraZ;
      updateCameraPosition();
    }
  }, []);

  // Reset view to default
  const resetView = useCallback(() => {
    controlsRef.current.cameraDistance = 5;
    controlsRef.current.cameraRotation = { theta: Math.PI / 4, phi: Math.PI / 4 };
    controlsRef.current.panOffset = { x: 0, y: 0, z: 0 };
    setModelRotation({ x: 0, y: 0, z: 0 });
    setModelScale(100);
    clearMeasurement();
    updateCameraPosition();
  }, []);

  // Clear measurement markers and line
  const clearMeasurement = useCallback(() => {
    // Remove markers
    measureMarkersRef.current.forEach(marker => {
      if (sceneRef.current) {
        sceneRef.current.remove(marker);
        marker.geometry.dispose();
        marker.material.dispose();
      }
    });
    measureMarkersRef.current = [];
    measureClickCountRef.current = 0; // Reset click counter
    
    // Remove line
    if (measureLineRef.current && sceneRef.current) {
      sceneRef.current.remove(measureLineRef.current);
      measureLineRef.current.geometry.dispose();
      measureLineRef.current.material.dispose();
      measureLineRef.current = null;
    }
    
    setMeasurePoints([]);
    setMeasureDistance(null);
  }, []);

  // Find nearest vertex to a point (for vertex snapping)
  const findNearestVertex = useCallback((intersectPoint, geometry, mesh) => {
    const positions = geometry.attributes.position;
    let nearestVertex = null;
    let nearestDistance = Infinity;
    
    // Transform intersect point to local space
    const localPoint = intersectPoint.clone();
    mesh.worldToLocal(localPoint);
    
    // Search all vertices
    for (let i = 0; i < positions.count; i++) {
      const vertex = new THREE.Vector3(
        positions.getX(i),
        positions.getY(i),
        positions.getZ(i)
      );
      
      const distance = localPoint.distanceTo(vertex);
      if (distance < nearestDistance) {
        nearestDistance = distance;
        nearestVertex = vertex.clone();
      }
    }
    
    // Transform back to world space
    if (nearestVertex) {
      mesh.localToWorld(nearestVertex);
    }
    
    return { vertex: nearestVertex, distance: nearestDistance };
  }, []);

  // Add measurement marker
  const addMeasureMarker = useCallback((point, color = 0xff4444) => {
    if (!sceneRef.current) return null;
    
    const geometry = new THREE.SphereGeometry(0.03, 16, 16);
    const material = new THREE.MeshBasicMaterial({ color });
    const marker = new THREE.Mesh(geometry, material);
    marker.position.copy(point);
    sceneRef.current.add(marker);
    measureMarkersRef.current.push(marker);
    
    return marker;
  }, []);

  // Draw measurement line between two points
  const drawMeasureLine = useCallback((point1, point2) => {
    if (!sceneRef.current) return;
    
    // Remove existing line
    if (measureLineRef.current) {
      sceneRef.current.remove(measureLineRef.current);
      measureLineRef.current.geometry.dispose();
      measureLineRef.current.material.dispose();
    }
    
    const geometry = new THREE.BufferGeometry().setFromPoints([point1, point2]);
    const material = new THREE.LineBasicMaterial({ 
      color: 0xff4444, 
      linewidth: 2,
      depthTest: false 
    });
    const line = new THREE.Line(geometry, material);
    line.renderOrder = 999; // Render on top
    sceneRef.current.add(line);
    measureLineRef.current = line;
  }, []);

  // Handle measure tool click
  const handleMeasureClick = useCallback((event) => {
    if (!meshRef.current || !cameraRef.current || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Calculate mouse position in normalized device coordinates
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );
    
    // Raycast
    raycasterRef.current.setFromCamera(mouse, cameraRef.current);
    const intersects = raycasterRef.current.intersectObject(meshRef.current);
    
    if (intersects.length > 0) {
      const intersect = intersects[0];
      
      // Find nearest vertex for snapping
      const { vertex } = findNearestVertex(
        intersect.point, 
        meshRef.current.geometry, 
        meshRef.current
      );
      
      const snappedPoint = vertex || intersect.point;
      const clickCount = measureClickCountRef.current;
      
      console.log('Measure click:', clickCount, 'markers:', measureMarkersRef.current.length);
      
      if (clickCount === 0) {
        // First point
        addMeasureMarker(snappedPoint, 0x44ff44); // Green for first point
        measureClickCountRef.current = 1;
        setMeasurePoints([snappedPoint.clone()]);
        setMeasureDistance(null);
      } else if (clickCount === 1) {
        // Second point - calculate distance
        addMeasureMarker(snappedPoint, 0xff4444); // Red for second point
        measureClickCountRef.current = 2;
        
        // Get first point from the marker
        const firstPoint = measureMarkersRef.current[0].position;
        drawMeasureLine(firstPoint, snappedPoint);
        
        // Calculate distance in scene units
        const sceneDistance = firstPoint.distanceTo(snappedPoint);
        console.log('Distance calculated:', sceneDistance);
        setMeasureDistance(sceneDistance);
        setMeasurePoints([firstPoint.clone(), snappedPoint.clone()]);
      } else {
        // Reset and start new measurement (third click clears)
        clearMeasurement();
        addMeasureMarker(snappedPoint, 0x44ff44);
        measureClickCountRef.current = 1;
        setMeasurePoints([snappedPoint.clone()]);
        setMeasureDistance(null);
      }
    }
  }, [findNearestVertex, addMeasureMarker, drawMeasureLine, clearMeasurement]);

  // Handle snap to face click - SIMPLIFIED APPROACH
  // Uses LOCAL face normal to calculate ABSOLUTE target rotation
  const handleSnapClick = useCallback((event) => {
    if (!meshRef.current || !cameraRef.current || !canvasRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Calculate mouse position in normalized device coordinates
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );
    
    // Raycast
    raycasterRef.current.setFromCamera(mouse, cameraRef.current);
    const intersects = raycasterRef.current.intersectObject(meshRef.current);
    
    if (intersects.length > 0) {
      const intersect = intersects[0];
      
      if (intersect.face) {
        // Get face normal in LOCAL space (geometry space)
        const localNormal = intersect.face.normal.clone().normalize();
        
        console.log('=== SNAP DEBUG ===');
        console.log('Local face normal:', localNormal.toArray());
        console.log('Current mesh rotation:', modelRotationRef.current);
        
        // Target: rotate mesh so this face's local normal points DOWN
        const downVector = new THREE.Vector3(0, -1, 0);
        const rotationQuat = new THREE.Quaternion();
        
        const dot = localNormal.dot(downVector);
        console.log('Dot(localNormal, down):', dot);
        
        if (dot > 0.999) {
          // Local normal already points down - target rotation is identity (0,0,0)
          rotationQuat.identity();
          console.log('Case: Local normal points down, target = identity');
        } else if (dot < -0.999) {
          // Local normal points UP, need 180° rotation around X
          rotationQuat.setFromAxisAngle(new THREE.Vector3(1, 0, 0), Math.PI);
          console.log('Case: Local normal points up, 180° around X');
        } else {
          // General case
          rotationQuat.setFromUnitVectors(localNormal, downVector);
          console.log('Case: General rotation');
        }
        
        // Convert to Euler - this is the ABSOLUTE target rotation
        const targetEuler = new THREE.Euler().setFromQuaternion(rotationQuat, 'XYZ');
        
        const newRotation = {
          x: Math.round(THREE.MathUtils.radToDeg(targetEuler.x)),
          y: Math.round(THREE.MathUtils.radToDeg(targetEuler.y)),
          z: Math.round(THREE.MathUtils.radToDeg(targetEuler.z))
        };
        
        console.log('Target rotation:', newRotation);
        console.log('=================');
        
        // ALWAYS apply the rotation (even if it seems like no change)
        setModelRotation(newRotation);
        
        // Reposition mesh to sit on grid after rotation is applied
        setTimeout(() => {
          if (meshRef.current) {
            meshRef.current.updateMatrixWorld(true);
            const box = new THREE.Box3().setFromObject(meshRef.current);
            meshRef.current.position.y -= box.min.y;
          }
        }, 50);
      }
    }
  }, []);

  // Highlight face on hover (for Snap mode visual feedback)
  const highlightFaceOnHover = useCallback((event) => {
    if (!meshRef.current || !cameraRef.current || !canvasRef.current || !sceneRef.current) return;
    
    const canvas = canvasRef.current;
    const rect = canvas.getBoundingClientRect();
    
    // Calculate mouse position in normalized device coordinates
    const mouse = new THREE.Vector2(
      ((event.clientX - rect.left) / rect.width) * 2 - 1,
      -((event.clientY - rect.top) / rect.height) * 2 + 1
    );
    
    // Raycast
    raycasterRef.current.setFromCamera(mouse, cameraRef.current);
    const intersects = raycasterRef.current.intersectObject(meshRef.current);
    
    if (intersects.length > 0) {
      const intersect = intersects[0];
      
      if (intersect.face) {
        // Get the three vertices of the face
        const geometry = meshRef.current.geometry;
        const positions = geometry.attributes.position;
        const a = intersect.face.a;
        const b = intersect.face.b;
        const c = intersect.face.c;
        
        // Create triangle geometry for highlight
        const highlightGeometry = new THREE.BufferGeometry();
        const vertices = new Float32Array([
          positions.getX(a), positions.getY(a), positions.getZ(a),
          positions.getX(b), positions.getY(b), positions.getZ(b),
          positions.getX(c), positions.getY(c), positions.getZ(c)
        ]);
        highlightGeometry.setAttribute('position', new THREE.BufferAttribute(vertices, 3));
        highlightGeometry.computeVertexNormals();
        
        // Remove existing highlight mesh
        if (highlightMeshRef.current) {
          sceneRef.current.remove(highlightMeshRef.current);
          highlightMeshRef.current.geometry.dispose();
          highlightMeshRef.current.material.dispose();
        }
        
        // Create new highlight mesh with bright color
        const highlightMaterial = new THREE.MeshBasicMaterial({
          color: 0x00ff88, // Bright green
          side: THREE.DoubleSide,
          transparent: true,
          opacity: 0.7,
          depthTest: false
        });
        
        const highlightMesh = new THREE.Mesh(highlightGeometry, highlightMaterial);
        
        // Copy the mesh's transformation so highlight aligns with the model
        highlightMesh.position.copy(meshRef.current.position);
        highlightMesh.rotation.copy(meshRef.current.rotation);
        highlightMesh.scale.copy(meshRef.current.scale);
        highlightMesh.renderOrder = 999; // Render on top
        
        sceneRef.current.add(highlightMesh);
        highlightMeshRef.current = highlightMesh;
      }
    } else {
      // Remove highlight when not hovering over mesh
      if (highlightMeshRef.current && sceneRef.current) {
        sceneRef.current.remove(highlightMeshRef.current);
        highlightMeshRef.current.geometry.dispose();
        highlightMeshRef.current.material.dispose();
        highlightMeshRef.current = null;
      }
    }
  }, []);

  // Clear highlight when snap mode is deactivated
  const clearFaceHighlight = useCallback(() => {
    if (highlightMeshRef.current && sceneRef.current) {
      sceneRef.current.remove(highlightMeshRef.current);
      highlightMeshRef.current.geometry.dispose();
      highlightMeshRef.current.material.dispose();
      highlightMeshRef.current = null;
    }
  }, []);

  // Handle canvas click for tools
  const handleCanvasClick = useCallback((event) => {
    const currentTool = activeToolRef.current;
    
    if (currentTool === TOOLS.MEASURE) {
      handleMeasureClick(event);
    } else if (currentTool === TOOLS.SNAP) {
      handleSnapClick(event);
    }
  }, [handleMeasureClick, handleSnapClick]);

  // Take screenshot
  const takeScreenshot = useCallback(() => {
    if (rendererRef.current && sceneRef.current && cameraRef.current) {
      rendererRef.current.render(sceneRef.current, cameraRef.current);
      const dataURL = rendererRef.current.domElement.toDataURL('image/png');
      const link = document.createElement('a');
      link.download = 'model-screenshot.png';
      link.href = dataURL;
      link.click();
    }
  }, []);

  // Toggle fullscreen
  const toggleFullscreen = useCallback(() => {
    const container = containerRef.current;
    if (!container) return;

    if (!document.fullscreenElement) {
      container.requestFullscreen().then(() => {
        setIsFullscreen(true);
      }).catch((err) => {
        console.error('Fullscreen error:', err);
      });
    } else {
      document.exitFullscreen().then(() => {
        setIsFullscreen(false);
      }).catch((err) => {
        console.error('Exit fullscreen error:', err);
      });
    }
  }, []);

  // Handle fullscreen change event
  useEffect(() => {
    const handleFullscreenChange = () => {
      const isNowFullscreen = !!document.fullscreenElement;
      setIsFullscreen(isNowFullscreen);
    };

    document.addEventListener('fullscreenchange', handleFullscreenChange);
    return () => document.removeEventListener('fullscreenchange', handleFullscreenChange);
  }, []);

  // Resize canvas based on viewport dimensions
  const resizeCanvas = useCallback((width, height) => {
    if (!rendererRef.current || !cameraRef.current || !canvasRef.current) return;
    if (width <= 0 || height <= 0) return;
    
    const canvas = canvasRef.current;
    const pixelRatio = window.devicePixelRatio || 1;
    
    console.log('Resizing renderer:', { width, height, isFullscreen, pixelRatio });
    
    // Set canvas buffer size
    canvas.width = Math.floor(width * pixelRatio);
    canvas.height = Math.floor(height * pixelRatio);
    
    // Set canvas display size
    canvas.style.width = width + 'px';
    canvas.style.height = height + 'px';
    
    // Update renderer
    rendererRef.current.setPixelRatio(pixelRatio);
    rendererRef.current.setSize(width, height, false);
    
    // Update camera
    cameraRef.current.aspect = width / height;
    cameraRef.current.updateProjectionMatrix();
    
    // Force render
    if (sceneRef.current) {
      rendererRef.current.render(sceneRef.current, cameraRef.current);
    }
  }, [isFullscreen]);

  // Force resize after fullscreen state changes (runs after React re-renders)
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    
    const doResize = () => {
      const rect = viewport.getBoundingClientRect();
      console.log('Fullscreen state changed, forcing resize:', { width: rect.width, height: rect.height, isFullscreen });
      resizeCanvas(Math.floor(rect.width), Math.floor(rect.height));
    };
    
    // Multiple attempts to catch the layout after CSS transition
    doResize();
    const t1 = setTimeout(doResize, 50);
    const t2 = setTimeout(doResize, 100);
    const t3 = setTimeout(doResize, 200);
    const t4 = setTimeout(doResize, 500);
    const t5 = setTimeout(doResize, 1000);
    
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
      clearTimeout(t4);
      clearTimeout(t5);
    };
  }, [isFullscreen, resizeCanvas]);

  // Use ResizeObserver to detect viewport size changes
  useEffect(() => {
    const viewport = viewportRef.current;
    if (!viewport) return;
    
    let resizeTimeout = null;
    
    const handleResize = () => {
      const rect = viewport.getBoundingClientRect();
      const width = Math.floor(rect.width);
      const height = Math.floor(rect.height);
      
      console.log('handleResize called:', { width, height, viewportExists: !!viewport });
      
      if (width > 0 && height > 0) {
        resizeCanvas(width, height);
      }
    };
    
    // Debounced handler to avoid rapid firing
    const debouncedResize = () => {
      if (resizeTimeout) cancelAnimationFrame(resizeTimeout);
      resizeTimeout = requestAnimationFrame(handleResize);
    };
    
    const resizeObserver = new ResizeObserver((entries) => {
      console.log('ResizeObserver fired:', entries.length, 'entries');
      debouncedResize();
    });
    resizeObserver.observe(viewport);
    
    // Also listen to window resize as backup
    const onWindowResize = () => {
      console.log('Window resize event fired');
      debouncedResize();
    };
    window.addEventListener('resize', onWindowResize);
    
    // Initial resize
    handleResize();
    
    return () => {
      resizeObserver.disconnect();
      window.removeEventListener('resize', onWindowResize);
      if (resizeTimeout) cancelAnimationFrame(resizeTimeout);
    };
  }, [resizeCanvas]);

  // Update camera position based on spherical coordinates and pan target
  const updateCameraPosition = useCallback(() => {
    if (cameraRef.current) {
      const { theta, phi } = controlsRef.current.cameraRotation;
      const distance = controlsRef.current.cameraDistance;
      const target = controlsRef.current.panOffset; // The point we orbit around and look at
      
      // Calculate camera position from spherical coordinates relative to target
      const x = target.x + distance * Math.sin(phi) * Math.cos(theta);
      const y = target.y + distance * Math.cos(phi);
      const z = target.z + distance * Math.sin(phi) * Math.sin(theta);
      
      cameraRef.current.position.set(x, y, z);
      cameraRef.current.lookAt(target.x, target.y, target.z);
    }
  }, []);

  // Unit conversion functions
  const convertVolumeToCm3 = (volume, fileUnits) => {
    const conversionFactors = {
      'mm': 0.001,      // 1 mm³ = 0.001 cm³
      'cm': 1.0,        // 1 cm³ = 1 cm³
      'm': 1000000.0,   // 1 m³ = 1,000,000 cm³
      'in': 16.387064,  // 1 in³ = 16.387064 cm³
      'ft': 28316.8466  // 1 ft³ = 28,316.8466 cm³
    };
    return volume * (conversionFactors[fileUnits] || 1.0);
  };

  const convertAreaToCm2 = (area, fileUnits) => {
    const conversionFactors = {
      'mm': 0.01,       // 1 mm² = 0.01 cm²
      'cm': 1.0,        // 1 cm² = 1 cm²
      'm': 10000.0,     // 1 m² = 10,000 cm²
      'in': 6.4516,     // 1 in² = 6.4516 cm²
      'ft': 929.0304    // 1 ft² = 929.0304 cm²
    };
    return area * (conversionFactors[fileUnits] || 1.0);
  };

  // Material densities (g/cm³)
  const getMaterialDensity = (materialType) => {
    const densities = {
      'PLA': 1.24,
      'ABS': 1.04,
      'PETG': 1.27,
      'TPU': 1.21,
      'WOOD_FILL': 1.15,
      'METAL_FILL': 1.50,
      'CARBON_FIBER': 1.30
    };
    return densities[materialType] || 1.24; // Default to PLA
  };

  // Infill percentages
  const getInfillPercentage = (strengthType) => {
    const infills = {
      'COSMETIC': 0.17,
      'Cosmetic Only': 0.17,      // 17%
      'REINFORCED': 0.27,
      'Reinforced': 0.27,    // 27%
      'FUNCTIONAL': 0.58,
      'Functional': 0.58     // 58%
    };
    return infills[strengthType] || 0.27; // Default to REINFORCED
  };

  // Calculate material weight
  const calculateMaterialWeight = (volume, materialType, strengthType) => {
    if (!volume || !materialType || !strengthType) return null;
    const density = getMaterialDensity(materialType);
    const infill = getInfillPercentage(strengthType);
    return volume * density * infill;
  };

  // Quality adjustment factors
  const getQualityAdjustmentFactor = (qualityType) => {
    const factors = {
      'Concept': 1.0,
      'Prototype': 1.35,
      'Test/Demo': 1.8,
      'End Use': 2.4,
      'Display': 4.0
    };
    return factors[qualityType] || 1.35;
  };

  // Calculate weight adjustment factor
  const calculateWeightAdjustmentFactor = (volumeAreaRatio) => {
    return -0.0865 * volumeAreaRatio + 4.3115;
  };

  // Calculate actual print weight
  const calculateActualPrintWeight = (requiredWeight, weightAdjustmentFactor) => {
    return requiredWeight * weightAdjustmentFactor;
  };

  // Calculate cost adjustment factor
  const calculateCostAdjustmentFactor = (actualPrintWeight) => {
    const factor = -0.0008 * actualPrintWeight + 9.7742;
    return Math.max(2.0, factor);  // Minimum of 2.0
  };

  // Calculate complexity adjustment factor
  const calculateComplexityAdjustmentFactor = (complexityPercentage) => {
    return 1 + complexityPercentage;
  };

  useEffect(() => {
    if (file) {
      // Use direct file (quote tool usage)
      loadModelFromFile();
    } else if (cadFileUrl) {
      // Load from URL (marketplace usage)
      loadModelFromUrl();
    }
  }, [file, cadFileUrl]);

  // Recalculate volume, surface area, and dimensions when units change
  useEffect(() => {
    if (fileInfo && fileInfo.rawVolume && fileInfo.rawSurfaceArea && fileInfo.rawDimensions) {
      // For STEP files, raw values are always in mm (OpenCASCADE converts internally)
      // For other files, raw values are in the user-selected units
      const rawUnits = fileInfo.rawOutputInMm ? 'mm' : units;
      
      // Recalculate with appropriate units
      const newVolume = convertVolumeToCm3(fileInfo.rawVolume, rawUnits);
      const newSurfaceArea = convertAreaToCm2(fileInfo.rawSurfaceArea, rawUnits);
      
      // Convert dimensions to cm based on actual raw units
      const convertDimensionToCm = (dim, fileUnits) => {
        if (fileUnits === 'mm') return dim / 10;
        if (fileUnits === 'cm') return dim;
        if (fileUnits === 'm') return dim * 100;
        if (fileUnits === 'in') return dim * 2.54;
        if (fileUnits === 'ft') return dim * 30.48;
        return dim; // Default to cm
      };
      
      // Update fileInfo with converted values
      setFileInfo(prev => ({
        ...prev,
        volume: newVolume,
        surfaceArea: newSurfaceArea,
        dimensions: {
          x: convertDimensionToCm(prev.rawDimensions.x, rawUnits).toFixed(2),
          y: convertDimensionToCm(prev.rawDimensions.y, rawUnits).toFixed(2),
          z: convertDimensionToCm(prev.rawDimensions.z, rawUnits).toFixed(2)
        }
      }));
      
      console.log(`Units changed to ${units} (raw: ${rawUnits}): Volume=${newVolume.toFixed(2)} cm³, Surface Area=${newSurfaceArea.toFixed(2)} cm², Dimensions updated`);
      
      // Notify parent of the updated analysis
      if (onAnalysis) {
        onAnalysis({
          ...fileInfo,
          volume: newVolume,
          surfaceArea: newSurfaceArea,
          dimensions: {
            x: convertDimensionToCm(fileInfo.rawDimensions.x, rawUnits).toFixed(2),
            y: convertDimensionToCm(fileInfo.rawDimensions.y, rawUnits).toFixed(2),
            z: convertDimensionToCm(fileInfo.rawDimensions.z, rawUnits).toFixed(2)
          }
        });
      }
    }
  }, [units]);

  const loadModelFromUrl = async () => {
    if (!cadFileUrl || !canvasRef.current) return;

    setPreviewState('loading');
    setErrorMessage('');

    try {
      // Fetch the CAD file from URL
      const response = await fetch(cadFileUrl);
      if (!response.ok) {
        throw new Error(`Failed to load CAD file: ${response.statusText}`);
      }
      
      const arrayBuffer = await response.arrayBuffer();
      const filename = cadFileUrl.split('/').pop() || 'model.stl';
      
      // Create a proper Blob for file conversion (needed for STEP/3MF)
      const blob = new Blob([arrayBuffer]);
      const file = new File([blob], filename, { type: 'application/octet-stream' });
      
      // Add arrayBuffer method for direct parsing compatibility
      file.arrayBuffer = () => Promise.resolve(arrayBuffer);
      
      await loadModel(file);
      
    } catch (error) {
      console.error('Failed to load CAD file from URL:', error);
      setErrorMessage(`Failed to load 3D model: ${error.message}`);
      setPreviewState('error');
    }
  };

  const loadModelFromFile = async () => {
    if (!file) return;
    await loadModel(file);
  };

  const loadModel = async (fileObj) => {
      setPreviewState('loading');
      setErrorMessage('');
      setActiveTool(TOOLS.ROTATE); // Reset to rotate tool

      try {
        // Setup Three.js scene
        const scene = new THREE.Scene();
        scene.background = new THREE.Color(0xf1f5f9); // slate-100
        sceneRef.current = scene;

        const camera = new THREE.PerspectiveCamera(50, 1, 0.1, 1000);
        camera.position.set(3, 3, 3);
        camera.lookAt(0, 0, 0);
        cameraRef.current = camera;

        const renderer = new THREE.WebGLRenderer({ 
          canvas: canvasRef.current,
          antialias: true,
          alpha: true,
          preserveDrawingBuffer: true // Required for screenshots
        });
        renderer.shadowMap.enabled = true;
        renderer.shadowMap.type = THREE.PCFSoftShadowMap;
        renderer.toneMapping = THREE.ACESFilmicToneMapping;
        renderer.toneMappingExposure = 1.0;
        rendererRef.current = renderer;
        
        // Get actual container size
        const container = canvasRef.current.parentElement;
        const containerWidth = container.clientWidth;
        const containerHeight = container.clientHeight;
        
        renderer.setSize(containerWidth, containerHeight);
        camera.aspect = containerWidth / containerHeight;
        camera.updateProjectionMatrix();

        // Enhanced lighting setup for better curvature visibility
        // Ambient light for overall illumination
        const ambientLight = new THREE.AmbientLight(0xffffff, 0.5);
        scene.add(ambientLight);
        
        // Key light (main directional light from top-right) - brighter
        const keyLight = new THREE.DirectionalLight(0xffffff, 1.4);
        keyLight.position.set(5, 10, 5);
        keyLight.castShadow = true;
        keyLight.shadow.mapSize.width = 2048;
        keyLight.shadow.mapSize.height = 2048;
        keyLight.shadow.camera.near = 0.5;
        keyLight.shadow.camera.far = 50;
        scene.add(keyLight);
        
        // Fill light (softer light from opposite side to reduce harsh shadows)
        const fillLight = new THREE.DirectionalLight(0xffffff, 0.8);
        fillLight.position.set(-5, 5, -5);
        scene.add(fillLight);
        
        // Rim light (back light to create edge definition) - stronger
        const rimLight = new THREE.DirectionalLight(0xffffff, 0.6);
        rimLight.position.set(0, 5, -8);
        scene.add(rimLight);
        
        // Bottom fill light to illuminate undersides
        const bottomLight = new THREE.DirectionalLight(0xffffff, 0.3);
        bottomLight.position.set(0, -5, 0);
        scene.add(bottomLight);
        
        // Add a subtle hemisphere light for natural sky/ground coloring
        const hemiLight = new THREE.HemisphereLight(0xddeeff, 0x303030, 0.4);
        scene.add(hemiLight);

        // Add grid helper (build plate representation)
        const gridHelper = new THREE.GridHelper(10, 20, 0x94a3b8, 0xcbd5e1);
        gridHelper.position.y = -0.01;
        scene.add(gridHelper);
        gridHelperRef.current = gridHelper;

        // Setup orientation indicator (axis gizmo) - professional CAD-style
        // Note: This will be initialized by a separate useEffect when canvas is ready
        
        // Store original file reference for save functionality
        originalFileRef.current = fileObj;
        
        // Reset change tracking
        initialRotationRef.current = { x: 0, y: 0, z: 0 };
        initialScaleRef.current = 100;
        setHasUnsavedChanges(false);
        setModelRotation({ x: 0, y: 0, z: 0 });
        setModelScale(100);

        // Parse STL file
        const filename = fileObj.name.toLowerCase();
        const extension = filename.split('.').pop();
        
        let geometry;
        let conversionInfo = null;
        
        // Check file type and handle conversion
        if (extension === 'step' || extension === 'stp') {
          console.log('Detected STEP file, using server conversion...');
          setIsStepFile(true);
          
          try {
            const conversionResult = await convertFileToSTL(fileObj, 'step');
            geometry = conversionResult.geometry;
            conversionInfo = {
              detectedUnits: conversionResult.detectedUnits,
              message: conversionResult.unitsDetectionMessage
            };
            console.log('STEP file conversion result:', conversionInfo);
          } catch (conversionError) {
            console.error('STEP conversion error:', conversionError);
            throw new Error(`STEP file conversion failed: ${conversionError.message}`);
          }
        } else if (extension === '3mf') {
          console.log('Detected 3MF file, using server conversion...');
          
          try {
            const conversionResult = await convertFileToSTL(fileObj, '3mf');
            geometry = conversionResult.geometry;
          } catch (conversionError) {
            console.error('3MF conversion error:', conversionError);
            throw new Error(`3MF file conversion failed: ${conversionError.message}`);
          }
        } else {
          // Handle native formats
          const arrayBuffer = await fileObj.arrayBuffer();
          
          if (extension === 'stl') {
            const loader = new STLLoader();
            geometry = loader.parse(arrayBuffer);
          } else if (extension === 'obj') {
            const loader = new OBJLoader();
            const text = new TextDecoder().decode(arrayBuffer);
            const obj = loader.parse(text);
            geometry = obj.children[0]?.geometry;
          } else if (extension === 'ply') {
            const loader = new PLYLoader();
            geometry = loader.parse(arrayBuffer);
          } else {
            throw new Error(`Unsupported file format: ${extension}. Supported formats: STL, OBJ, PLY, STEP/STP, 3MF`);
          }
        }

        // Reset STEP file flag after processing
        setIsStepFile(false);

        if (!geometry) {
          throw new Error('Unable to parse 3D model geometry');
        }

        // Center and normalize the geometry
        geometry.computeBoundingBox();
        const box = geometry.boundingBox;
        const center = new THREE.Vector3();
        box.getCenter(center);
        geometry.translate(-center.x, -center.y, -center.z);

        // Scale to fit view
        const size = new THREE.Vector3();
        box.getSize(size);
        const maxDim = Math.max(size.x, size.y, size.z);
        const scale = 2 / maxDim;
        geometry.scale(scale, scale, scale);

        // Recalculate bounding box after centering
        geometry.computeBoundingBox();
        const newBox = geometry.boundingBox;
        
        // Position model so it sits on the grid (bottom at y=0)
        const minY = newBox.min.y;
        geometry.translate(0, -minY, 0);

        // Create material with vibrant blue color and better visual properties
        const meshMaterial = new THREE.MeshPhongMaterial({
          color: 0x2563eb, // Vibrant blue (blue-600)
          specular: 0x4488ff,
          shininess: 80,
          flatShading: false,
          side: THREE.DoubleSide
        });

        // Compute vertex normals for smooth shading
        geometry.computeVertexNormals();

        const mesh = new THREE.Mesh(geometry, meshMaterial);
        mesh.castShadow = true;
        mesh.receiveShadow = true;
        scene.add(mesh);
        meshRef.current = mesh;
        
        // Store original scale for the scale tool (mesh starts at scale 1)
        originalScaleRef.current = 1;
        setModelScale(100); // Reset scale to 100% for new model

        // Calculate real mesh volume and surface area
        const rawVolume = calculateMeshVolume(geometry) / (scale * scale * scale);
        const rawSurfaceArea = calculateMeshSurfaceArea(geometry) / (scale * scale);
        
        // Get raw dimensions (in original units)
        const rawDimensions = {
          x: size.x,
          y: size.y,
          z: size.z
        };
        
        // For STEP files, values are always in mm. Otherwise, use user-selected units
        const rawOutputInMm = (extension === 'step' || extension === 'stp');
        const rawUnits = rawOutputInMm ? 'mm' : units;
        
        // Convert to cm³ and cm² for display
        const volumeCm3 = convertVolumeToCm3(rawVolume, rawUnits);
        const surfaceAreaCm2 = convertAreaToCm2(rawSurfaceArea, rawUnits);
        
        // Convert dimensions to cm
        const convertDimensionToCm = (dim, fileUnits) => {
          if (fileUnits === 'mm') return dim / 10;
          if (fileUnits === 'cm') return dim;
          if (fileUnits === 'm') return dim * 100;
          if (fileUnits === 'in') return dim * 2.54;
          if (fileUnits === 'ft') return dim * 30.48;
          return dim;
        };

        // Calculate analysis
        const analysis = {
          volume: volumeCm3,
          surfaceArea: surfaceAreaCm2,
          complexity: surfaceAreaCm2 / (volumeCm3 || 1),
          fileSize: fileObj.size,
          dimensions: {
            x: convertDimensionToCm(rawDimensions.x, rawUnits).toFixed(2),
            y: convertDimensionToCm(rawDimensions.y, rawUnits).toFixed(2),
            z: convertDimensionToCm(rawDimensions.z, rawUnits).toFixed(2)
          },
          // Store raw values for unit conversion
          rawVolume,
          rawSurfaceArea,
          rawDimensions,
          rawOutputInMm
        };
        
        // Log detected units for STEP files
        if (conversionInfo) {
          console.log(`STEP file detected units: ${conversionInfo.detectedUnits}`);
          if (conversionInfo.message) {
            console.log(`Units detection message: ${conversionInfo.message}`);
          }
        }
        
        console.log(`Raw values (in ${rawUnits}): Volume=${rawVolume.toFixed(2)} ${rawUnits}³, Surface Area=${rawSurfaceArea.toFixed(2)} ${rawUnits}², Dimensions=${rawDimensions.x.toFixed(2)}×${rawDimensions.y.toFixed(2)}×${rawDimensions.z.toFixed(2)} ${rawUnits}`);
        console.log(`Converted to cm: Volume=${volumeCm3.toFixed(2)} cm³, Surface Area=${surfaceAreaCm2.toFixed(2)} cm², Dimensions=${analysis.dimensions.x}×${analysis.dimensions.y}×${analysis.dimensions.z} cm`);

        setFileInfo(analysis);
        setPreviewState('success');
        if (onAnalysis) onAnalysis(analysis);

        // Setup controls
        const canvas = canvasRef.current;
        let isDragging = false;
        let hasDragged = false; // Track if mouse moved significantly during press
        let previousMousePosition = { x: 0, y: 0 };
        let mouseDownPosition = { x: 0, y: 0 };

        const onMouseDown = (e) => {
          isDragging = true;
          hasDragged = false;
          previousMousePosition = { x: e.clientX, y: e.clientY };
          mouseDownPosition = { x: e.clientX, y: e.clientY };
          const currentTool = activeToolRef.current;
          // Use crosshair for snap/measure, grabbing for others
          canvas.style.cursor = (currentTool === TOOLS.SNAP || currentTool === TOOLS.MEASURE) ? 'crosshair' : 'grabbing';
        };

        const onMouseMove = (e) => {
          const currentTool = activeToolRef.current;
          
          // Face highlighting for Snap mode (even when not dragging)
          if (!isDragging && currentTool === TOOLS.SNAP) {
            highlightFaceOnHover(e);
            return;
          }
          
          if (!isDragging) return;
          
          const deltaX = e.clientX - previousMousePosition.x;
          const deltaY = e.clientY - previousMousePosition.y;
          
          // Check if we've dragged more than 3 pixels (to distinguish click from drag)
          const totalDelta = Math.abs(e.clientX - mouseDownPosition.x) + Math.abs(e.clientY - mouseDownPosition.y);
          if (totalDelta > 3) {
            hasDragged = true;
          }

          if (currentTool === TOOLS.ROTATE || currentTool === TOOLS.SNAP || currentTool === TOOLS.MEASURE) {
            // Orbit camera around the model
            controlsRef.current.cameraRotation.theta += deltaX * 0.01;
            // Inverted: drag up = look down (decrease phi), drag down = look up (increase phi)
            controlsRef.current.cameraRotation.phi -= deltaY * 0.01;
            
            // Clamp phi to prevent flipping
            controlsRef.current.cameraRotation.phi = Math.max(0.1, Math.min(Math.PI - 0.1, controlsRef.current.cameraRotation.phi));
            
            updateCameraPosition();
          } else if (currentTool === TOOLS.PAN) {
            // Pan by moving the camera target (the point camera orbits around)
            const panSpeed = 0.005 * controlsRef.current.cameraDistance;
            
            // Get camera's right and up vectors for proper panning direction
            const right = new THREE.Vector3();
            const up = new THREE.Vector3();
            right.setFromMatrixColumn(camera.matrix, 0);
            up.setFromMatrixColumn(camera.matrix, 1);
            
            // Move the target point - dragging right moves target left (so view shifts right)
            controlsRef.current.panOffset.x -= right.x * deltaX * panSpeed - up.x * deltaY * panSpeed;
            controlsRef.current.panOffset.y -= right.y * deltaX * panSpeed - up.y * deltaY * panSpeed;
            controlsRef.current.panOffset.z -= right.z * deltaX * panSpeed - up.z * deltaY * panSpeed;
            
            updateCameraPosition();
          } else if (currentTool === TOOLS.ZOOM) {
            // Zoom with vertical drag
            controlsRef.current.cameraDistance += deltaY * 0.02;
            controlsRef.current.cameraDistance = Math.max(1, Math.min(20, controlsRef.current.cameraDistance));
            updateCameraPosition();
          }

          previousMousePosition = { x: e.clientX, y: e.clientY };
        };

        const onMouseUp = (e) => {
          const currentTool = activeToolRef.current;
          
          // If we didn't drag and we're in snap/measure mode, handle the click
          if (!hasDragged && (currentTool === TOOLS.SNAP || currentTool === TOOLS.MEASURE)) {
            handleCanvasClick(e);
          }
          
          isDragging = false;
          hasDragged = false;
          // Use crosshair for snap/measure, grab for others
          canvas.style.cursor = (currentTool === TOOLS.SNAP || currentTool === TOOLS.MEASURE) ? 'crosshair' : 'grab';
        };

        const onWheel = (e) => {
          e.preventDefault();
          controlsRef.current.cameraDistance += e.deltaY * 0.005;
          controlsRef.current.cameraDistance = Math.max(1, Math.min(20, controlsRef.current.cameraDistance));
          updateCameraPosition();
        };

        // Touch support
        let touchStartDistance = 0;
        const onTouchStart = (e) => {
          if (e.touches.length === 1) {
            isDragging = true;
            previousMousePosition = { x: e.touches[0].clientX, y: e.touches[0].clientY };
          } else if (e.touches.length === 2) {
            touchStartDistance = Math.hypot(
              e.touches[0].clientX - e.touches[1].clientX,
              e.touches[0].clientY - e.touches[1].clientY
            );
          }
        };

        const onTouchMove = (e) => {
          e.preventDefault();
          if (e.touches.length === 1 && isDragging) {
            const deltaX = e.touches[0].clientX - previousMousePosition.x;
            const deltaY = e.touches[0].clientY - previousMousePosition.y;

            controlsRef.current.cameraRotation.theta += deltaX * 0.01;
            // Inverted: same as mouse
            controlsRef.current.cameraRotation.phi -= deltaY * 0.01;
            controlsRef.current.cameraRotation.phi = Math.max(0.1, Math.min(Math.PI - 0.1, controlsRef.current.cameraRotation.phi));
            
            updateCameraPosition();
            previousMousePosition = { x: e.touches[0].clientX, y: e.touches[0].clientY };
          } else if (e.touches.length === 2) {
            const currentDistance = Math.hypot(
              e.touches[0].clientX - e.touches[1].clientX,
              e.touches[0].clientY - e.touches[1].clientY
            );
            const delta = touchStartDistance - currentDistance;
            controlsRef.current.cameraDistance += delta * 0.01;
            controlsRef.current.cameraDistance = Math.max(1, Math.min(20, controlsRef.current.cameraDistance));
            updateCameraPosition();
            touchStartDistance = currentDistance;
          }
        };

        const onTouchEnd = () => {
          isDragging = false;
        };

        canvas.addEventListener('mousedown', onMouseDown);
        canvas.addEventListener('mousemove', onMouseMove);
        canvas.addEventListener('mouseup', onMouseUp);
        canvas.addEventListener('mouseleave', onMouseUp);
        canvas.addEventListener('wheel', onWheel, { passive: false });
        canvas.addEventListener('touchstart', onTouchStart, { passive: false });
        canvas.addEventListener('touchmove', onTouchMove, { passive: false });
        canvas.addEventListener('touchend', onTouchEnd);
        canvas.style.cursor = 'grab';

        // Initialize camera position
        updateCameraPosition();

        // Animation loop
        const animate = () => {
          requestAnimationFrame(animate);
          renderer.render(scene, camera);
          
          // Update axis indicator to match main camera orientation
          if (axisRendererRef.current && axisCameraRef.current && axisSceneRef.current) {
            // Copy camera rotation to axis camera (inverted to show world orientation)
            axisCameraRef.current.position.copy(camera.position).normalize().multiplyScalar(2);
            axisCameraRef.current.lookAt(0, 0, 0);
            axisCameraRef.current.up.copy(camera.up);
            axisRendererRef.current.render(axisSceneRef.current, axisCameraRef.current);
          }
        };
        animate();

        // Cleanup
        return () => {
          canvas?.removeEventListener('mousedown', onMouseDown);
          canvas?.removeEventListener('mousemove', onMouseMove);
          canvas?.removeEventListener('mouseup', onMouseUp);
          canvas?.removeEventListener('wheel', onWheel);
          canvas?.removeEventListener('touchstart', onTouchStart);
          canvas?.removeEventListener('touchmove', onTouchMove);
          canvas?.removeEventListener('touchend', onTouchEnd);
          renderer.dispose();
          
          // Cleanup axis indicator
          if (axisRendererRef.current) {
            axisRendererRef.current.dispose();
          }
        };

      } catch (error) {
        console.error('3D Error:', error);
        
        // Reset STEP file flag
        setIsStepFile(false);
        
        // Create user-friendly error message
        const fileExtension = fileObj.name.toLowerCase().split('.').pop();
        let userMessage = '';
        if (error.message.includes('Server conversion failed')) {
          userMessage = 'Preview temporarily unavailable due to server processing. Your file was uploaded successfully and will be analyzed for pricing.';
        } else if (error.message.includes('not available') || error.message.includes('coming soon')) {
          userMessage = error.message;
        } else {
          userMessage = `Preview not available for this file type. Your ${fileExtension?.toUpperCase() || 'file'} was uploaded successfully and will be analyzed for accurate pricing.`;
        }
        
        setErrorMessage(userMessage);
        setPreviewState('error');
        
        // Provide fallback analysis so quote can continue
        const fallback = {
          volume: Math.max(1.0, fileObj.size / 100000),
          surfaceArea: Math.max(10.0, fileObj.size / 50000),
          complexity: 1.5,
          fileSize: fileObj.size
        };
        if (onAnalysis) onAnalysis(fallback);
      }
    };

  // Tool button component
  const ToolButton = ({ tool, icon: Icon, title, isActive, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className={`w-8 h-8 rounded-md flex items-center justify-center transition-all duration-150 ${
        isActive 
          ? 'bg-[#0007b5] text-white shadow-sm' 
          : 'text-slate-500 hover:bg-white hover:text-slate-700 hover:shadow-sm'
      }`}
      title={title}
      data-testid={`tool-${tool}`}
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  // Toggle button component (for wireframe, grid)
  const ToggleButton = ({ icon: Icon, title, isActive, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className={`w-8 h-8 rounded-md flex items-center justify-center transition-all duration-150 ${
        isActive 
          ? 'bg-white shadow-sm ring-1 ring-slate-200 text-[#0007b5]' 
          : 'text-slate-500 hover:bg-white hover:text-slate-700 hover:shadow-sm'
      }`}
      title={title}
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  // Action button component (for screenshot, fullscreen, etc.)
  const ActionButton = ({ icon: Icon, title, onClick }) => (
    <button
      type="button"
      onClick={onClick}
      className="w-8 h-8 rounded-md flex items-center justify-center text-slate-500 hover:bg-white hover:text-slate-700 hover:shadow-sm transition-all duration-150"
      title={title}
    >
      <Icon className="w-4 h-4" />
    </button>
  );

  return (
    <div ref={containerRef} className={`relative w-full ${className || ''} ${isFullscreen ? 'fixed inset-0 z-50 bg-slate-900 flex flex-col' : ''}`}>
      {/* Professional Toolbar */}
      {previewState === 'success' && (
        <div className={`px-3 py-2 bg-slate-50 border-b border-slate-200 flex items-center justify-between flex-shrink-0 ${isFullscreen ? '' : 'rounded-t-xl'}`}>
          <div className="flex items-center gap-4">
            {/* Logo */}
            <div className="flex items-center gap-2 pr-3 border-r border-slate-300">
              <img 
                src="/logo-icon-blue.png" 
                alt="Create 3D Works" 
                className="w-6 h-6 object-contain"
              />
            </div>
            
            {/* Navigation Group */}
            <div className="flex items-center gap-1">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mr-2">Navigate</span>
              <ToolButton 
                tool={TOOLS.ROTATE} 
                icon={RefreshCw} 
                title="Rotate" 
                isActive={activeTool === TOOLS.ROTATE}
                onClick={() => setActiveTool(TOOLS.ROTATE)}
              />
              <ToolButton 
                tool={TOOLS.PAN} 
                icon={Hand} 
                title="Pan" 
                isActive={activeTool === TOOLS.PAN}
                onClick={() => setActiveTool(TOOLS.PAN)}
              />
              <ToolButton 
                tool={TOOLS.ZOOM} 
                icon={ZoomIn} 
                title="Zoom" 
                isActive={activeTool === TOOLS.ZOOM}
                onClick={() => setActiveTool(TOOLS.ZOOM)}
              />
            </div>
            
            {/* Divider */}
            <div className="w-px h-6 bg-slate-300"></div>
            
            {/* Model Tools Group */}
            <div className="flex items-center gap-1 relative">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mr-2">Model</span>
              
              {/* Rotate Model Button with Dropdown */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowRotatePanel(!showRotatePanel)}
                  className={`w-8 h-8 rounded-md flex items-center justify-center transition-all duration-150 ${
                    showRotatePanel 
                      ? 'bg-[#0007b5] text-white shadow-sm' 
                      : 'text-slate-500 hover:bg-white hover:text-slate-700 hover:shadow-sm'
                  }`}
                  title="Rotate Model"
                >
                  <Rotate3d className="w-4 h-4" />
                </button>
                
                {/* Rotate Panel Dropdown */}
                {showRotatePanel && (
                  <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-200 p-4 z-50 w-72">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-slate-900 text-sm">Rotate Model</h4>
                      <button
                        type="button"
                        onClick={handleResetModelRotation}
                        className="text-xs text-[#0007b5] hover:text-[#0015e6] font-medium"
                      >
                        Reset
                      </button>
                    </div>
                    
                    {/* X Axis */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-red-600">X Axis</span>
                        <span className="text-xs text-slate-500">{modelRotation.x.toFixed(0)}°</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleRotate90('x', -1)}
                          className="w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
                        >
                          <ChevronLeft className="w-4 h-4 text-slate-600" />
                        </button>
                        <input
                          type="range"
                          min="-180"
                          max="180"
                          value={modelRotation.x}
                          onChange={(e) => handleSliderChange('x', e.target.value)}
                          className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-red-500"
                        />
                        <button
                          type="button"
                          onClick={() => handleRotate90('x', 1)}
                          className="w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
                        >
                          <ChevronRight className="w-4 h-4 text-slate-600" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Y Axis */}
                    <div className="mb-3">
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-green-600">Y Axis</span>
                        <span className="text-xs text-slate-500">{modelRotation.y.toFixed(0)}°</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleRotate90('y', -1)}
                          className="w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
                        >
                          <ChevronLeft className="w-4 h-4 text-slate-600" />
                        </button>
                        <input
                          type="range"
                          min="-180"
                          max="180"
                          value={modelRotation.y}
                          onChange={(e) => handleSliderChange('y', e.target.value)}
                          className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-green-500"
                        />
                        <button
                          type="button"
                          onClick={() => handleRotate90('y', 1)}
                          className="w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
                        >
                          <ChevronRight className="w-4 h-4 text-slate-600" />
                        </button>
                      </div>
                    </div>
                    
                    {/* Z Axis */}
                    <div>
                      <div className="flex items-center justify-between mb-1">
                        <span className="text-xs font-medium text-blue-600">Z Axis</span>
                        <span className="text-xs text-slate-500">{modelRotation.z.toFixed(0)}°</span>
                      </div>
                      <div className="flex items-center gap-2">
                        <button
                          type="button"
                          onClick={() => handleRotate90('z', -1)}
                          className="w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
                        >
                          <ChevronLeft className="w-4 h-4 text-slate-600" />
                        </button>
                        <input
                          type="range"
                          min="-180"
                          max="180"
                          value={modelRotation.z}
                          onChange={(e) => handleSliderChange('z', e.target.value)}
                          className="flex-1 h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-blue-500"
                        />
                        <button
                          type="button"
                          onClick={() => handleRotate90('z', 1)}
                          className="w-7 h-7 rounded bg-slate-100 hover:bg-slate-200 flex items-center justify-center"
                        >
                          <ChevronRight className="w-4 h-4 text-slate-600" />
                        </button>
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              <ToolButton 
                tool={TOOLS.SNAP} 
                icon={Magnet} 
                title="Snap to Face" 
                isActive={activeTool === TOOLS.SNAP}
                onClick={() => setActiveTool(TOOLS.SNAP)}
              />
              
              {/* Scale Model Button with Dropdown */}
              <div className="relative">
                <button
                  type="button"
                  onClick={() => setShowScalePanel(!showScalePanel)}
                  className={`w-8 h-8 rounded-md flex items-center justify-center transition-all duration-150 ${
                    showScalePanel 
                      ? 'bg-[#0007b5] text-white shadow-sm' 
                      : 'text-slate-500 hover:bg-white hover:text-slate-700 hover:shadow-sm'
                  }`}
                  title="Scale Model"
                >
                  <Expand className="w-4 h-4" />
                </button>
                
                {/* Scale Panel Dropdown */}
                {showScalePanel && (
                  <div className="absolute top-full left-0 mt-2 bg-white rounded-xl shadow-xl border border-slate-200 p-4 z-50 w-64">
                    <div className="flex items-center justify-between mb-3">
                      <h4 className="font-semibold text-slate-900 text-sm">Scale Model</h4>
                      <button
                        type="button"
                        onClick={handleResetScale}
                        className="text-xs text-[#0007b5] hover:text-[#0015e6] font-medium"
                      >
                        Reset
                      </button>
                    </div>
                    
                    {/* Scale Slider */}
                    <div className="mb-4">
                      <div className="flex items-center justify-between mb-2">
                        <span className="text-xs font-medium text-slate-600">Scale</span>
                        <div className="flex items-center gap-1">
                          <input
                            type="number"
                            min="10"
                            max="500"
                            value={modelScale}
                            onChange={(e) => handleScaleChange(e.target.value)}
                            className="w-16 px-2 py-1 text-xs text-right border border-slate-200 rounded focus:outline-none focus:ring-1 focus:ring-[#0007b5]"
                          />
                          <span className="text-xs text-slate-500">%</span>
                        </div>
                      </div>
                      <input
                        type="range"
                        min="10"
                        max="500"
                        value={modelScale}
                        onChange={(e) => handleScaleChange(e.target.value)}
                        className="w-full h-2 bg-slate-200 rounded-lg appearance-none cursor-pointer accent-[#0007b5]"
                      />
                      <div className="flex justify-between text-[10px] text-slate-400 mt-1">
                        <span>10%</span>
                        <span>100%</span>
                        <span>500%</span>
                      </div>
                    </div>
                    
                    {/* Quick Presets */}
                    <div>
                      <span className="text-xs font-medium text-slate-600 block mb-2">Quick Presets</span>
                      <div className="grid grid-cols-4 gap-2">
                        {[50, 75, 100, 150, 200, 250, 300, 400].map((preset) => (
                          <button
                            key={preset}
                            type="button"
                            onClick={() => handleScalePreset(preset)}
                            className={`px-2 py-1.5 text-xs rounded font-medium transition-colors ${
                              modelScale === preset
                                ? 'bg-[#0007b5] text-white'
                                : 'bg-slate-100 text-slate-600 hover:bg-slate-200'
                            }`}
                          >
                            {preset}%
                          </button>
                        ))}
                      </div>
                    </div>
                  </div>
                )}
              </div>
              
              {/* Measure Tool - Hidden for now */}
              {/* <ToolButton 
                tool={TOOLS.MEASURE} 
                icon={Ruler} 
                title="Measure" 
                isActive={activeTool === TOOLS.MEASURE}
                onClick={() => setActiveTool(TOOLS.MEASURE)}
              /> */}
            </div>
            
            {/* Divider */}
            <div className="w-px h-6 bg-slate-300"></div>
            
            {/* View Group */}
            <div className="flex items-center gap-1">
              <span className="text-[10px] uppercase tracking-wider text-slate-400 font-semibold mr-2">View</span>
              <ToggleButton 
                icon={Box} 
                title="Wireframe" 
                isActive={showWireframe}
                onClick={toggleWireframe}
              />
              <ToggleButton 
                icon={Grid3X3} 
                title="Toggle Grid" 
                isActive={showGrid}
                onClick={toggleGrid}
              />
              <ActionButton 
                icon={Maximize2} 
                title="Fit to View" 
                onClick={fitToView}
              />
              <ActionButton 
                icon={RotateCcw} 
                title="Reset View" 
                onClick={resetView}
              />
            </div>
          </div>
          
          {/* Right Side Utilities */}
          <div className="flex items-center gap-1">
            {/* Save Changes Button - Highlighted when there are unsaved changes */}
            <button
              onClick={saveModifiedModel}
              disabled={!hasUnsavedChanges || isSaving}
              title={hasUnsavedChanges ? "Save model changes" : "No changes to save"}
              className={`p-2 rounded-lg transition-all duration-200 flex items-center gap-1.5 ${
                hasUnsavedChanges 
                  ? 'bg-green-500 hover:bg-green-600 text-white shadow-md' 
                  : 'bg-slate-100 text-slate-400 cursor-not-allowed'
              }`}
            >
              <Save className="w-4 h-4" />
              <span className="text-xs font-medium">
                {isSaving ? 'Saving...' : 'Save'}
              </span>
            </button>
            
            <div className="w-px h-6 bg-slate-300 mx-1"></div>
            
            <ActionButton 
              icon={Camera} 
              title="Screenshot" 
              onClick={takeScreenshot}
            />
            <ActionButton 
              icon={Maximize} 
              title="Fullscreen" 
              onClick={toggleFullscreen}
            />
          </div>
        </div>
      )}

      {/* 3D Viewport */}
      <div 
        ref={viewportRef}
        className={`w-full bg-gradient-to-b from-slate-100 to-slate-200 ${previewState === 'success' ? '' : 'rounded-t-xl'} overflow-hidden relative ${isFullscreen ? 'flex-1' : 'h-[550px]'}`}
      >
        <canvas 
          ref={canvasRef}
          className="block touch-none absolute top-0 left-0"
        />
        
        {/* Active Tool Hint */}
        {previewState === 'success' && (
          <div className="absolute top-4 left-4 bg-[#0007b5] text-white text-xs font-medium px-3 py-1.5 rounded-full shadow-lg">
            {TOOL_HINTS[activeTool]}
          </div>
        )}

        {/* Professional Axis Indicator (CAD-style orientation gizmo) */}
        {previewState === 'success' && (
          <div className="absolute bottom-4 left-4 w-20 h-20">
            <canvas 
              ref={axisCanvasRef} 
              width={80} 
              height={80}
              className="w-full h-full"
            />
          </div>
        )}

        {/* Dimensions Display - Updated based on scale */}
        {previewState === 'success' && fileInfo?.dimensions && (
          <div className="absolute bottom-4 right-4 bg-white/80 backdrop-blur-sm rounded-lg shadow-md px-3 py-2">
            <div className="text-xs font-medium text-slate-600 space-y-0.5">
              <div className="flex justify-between gap-3">
                <span className="text-red-500">X:</span>
                <span className="text-slate-900">{(parseFloat(fileInfo.dimensions.x) * modelScale / 100).toFixed(2)} cm</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-green-500">Y:</span>
                <span className="text-slate-900">{(parseFloat(fileInfo.dimensions.y) * modelScale / 100).toFixed(2)} cm</span>
              </div>
              <div className="flex justify-between gap-3">
                <span className="text-blue-500">Z:</span>
                <span className="text-slate-900">{(parseFloat(fileInfo.dimensions.z) * modelScale / 100).toFixed(2)} cm</span>
              </div>
              {modelScale !== 100 && (
                <div className="text-[10px] text-slate-400 mt-1 pt-1 border-t border-slate-200">
                  Scale: {modelScale}%
                </div>
              )}
            </div>
          </div>
        )}

        {/* Measurement Display */}
        {previewState === 'success' && activeTool === TOOLS.MEASURE && (
          <div className="absolute top-16 left-4 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg px-4 py-3 min-w-[200px]">
            <div className="flex items-center justify-between mb-2">
              <h4 className="text-sm font-semibold text-slate-900">Measure Tool</h4>
              {measurePoints.length > 0 && (
                <button
                  onClick={clearMeasurement}
                  className="text-xs text-red-500 hover:text-red-700 font-medium"
                >
                  Clear
                </button>
              )}
            </div>
            
            {measurePoints.length === 0 && (
              <p className="text-xs text-slate-500">Click on model to set first point</p>
            )}
            
            {measurePoints.length === 1 && (
              <p className="text-xs text-slate-500">Click to set second point</p>
            )}
            
            {measureDistance !== null && (
              <div className="mt-2 pt-2 border-t border-slate-200">
                <div className="text-xs text-slate-500 mb-1">Distance:</div>
                <div className="text-lg font-bold text-[#0007b5]">
                  {(measureDistance * 100).toFixed(2)} <span className="text-sm font-normal">units</span>
                </div>
                <p className="text-[10px] text-slate-400 mt-1">
                  (Relative to normalized model scale)
                </p>
              </div>
            )}
          </div>
        )}

        {/* Snap to Face Instructions */}
        {previewState === 'success' && activeTool === TOOLS.SNAP && (
          <div className="absolute top-16 left-4 bg-white/95 backdrop-blur-sm rounded-lg shadow-lg px-4 py-3 min-w-[200px]">
            <h4 className="text-sm font-semibold text-slate-900 mb-2">Snap to Face</h4>
            <p className="text-xs text-slate-500">
              Click on any face of the model to rotate it flat onto the build plate.
            </p>
          </div>
        )}
        
        {previewState === 'idle' && (
          <div className="absolute inset-0 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Box className="w-8 h-8 text-blue-600" />
              </div>
              <p className="text-gray-600">Upload a 3D file to see preview</p>
            </div>
          </div>
        )}
        
        {previewState === 'loading' && (
          <div className="absolute inset-0 flex items-center justify-center bg-blue-50/90 z-10">
            <div className="text-center max-w-md px-6">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className="text-blue-600 font-medium mb-2">
                {isStepFile ? 'Loading STEP file preview...' : 'Loading 3D preview...'}
              </p>
              {isStepFile && (
                <div className="bg-blue-100 rounded-lg p-4 border border-blue-200 mt-3">
                  <p className="text-sm text-blue-800">
                    <strong>STEP files may take a few minutes to process.</strong>
                    <br />
                    Large files (over 20MB) may take up to 3 minutes.
                  </p>
                  <p className="text-xs text-blue-700 mt-2">
                    Our system is processing your STEP/STP file for preview. Please wait...
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
        
        {previewState === 'error' && (
          <div className="absolute inset-0 flex items-center justify-center bg-yellow-50/95 z-10">
            <div className="text-center max-w-sm p-6">
              <div className="w-16 h-16 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-4">
                <Box className="w-8 h-8 text-blue-600" />
              </div>
              <h3 className="font-semibold text-slate-900 mb-3">3D Preview Unavailable</h3>
              <p className="text-sm text-slate-600 mb-4">{errorMessage}</p>
              <div className="bg-green-100 rounded-lg p-4 border border-green-200">
                <div className="flex items-center space-x-2">
                  <span className="text-green-600 text-lg">✓</span>
                  <div className="text-left">
                    <p className="text-sm font-medium text-green-800">File Upload Successful</p>
                    <p className="text-xs text-green-700">Continue with quote parameters below</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>

      {fileInfo && !isFullscreen && (
        <div className="bg-blue-50 rounded-b-xl p-4 border-t border-blue-100">
          <h4 className="font-semibold text-blue-900 mb-3">3D Model Analysis</h4>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-3 text-sm">
            {/* File Size */}
            <div className="whitespace-nowrap">
              <span className="text-blue-700 font-medium">File Size:</span>
              <span className="ml-2 text-blue-900">
                {(() => {
                  const fileSizeMB = fileInfo.fileSize / 1024 / 1024;
                  if (fileSizeMB < 1.0) {
                    return `${(fileInfo.fileSize / 1024).toFixed(1)} KB`;
                  }
                  return `${fileSizeMB.toFixed(1)} MB`;
                })()}
              </span>
            </div>
            
            {/* Volume */}
            <div className="whitespace-nowrap">
              <span className="text-blue-700 font-medium">Volume:</span>
              <span className="ml-2 text-blue-900">{fileInfo.volume.toFixed(2)} cm³</span>
            </div>
            
            {/* Dimensions in cm (L x W x H) */}
            {fileInfo.dimensions && (
              <div className="whitespace-nowrap">
                <span className="text-blue-700 font-medium">Dimensions (L×W×H):</span>
                <span className="ml-2 text-blue-900">
                  {fileInfo.dimensions.x} × {fileInfo.dimensions.y} × {fileInfo.dimensions.z} cm
                </span>
              </div>
            )}
            
            {/* Dimensions in inches (L x W x H) */}
            {fileInfo.dimensions && (
              <div className="whitespace-nowrap">
                <span className="text-blue-700 font-medium">Dimensions (L×W×H):</span>
                <span className="ml-2 text-blue-900">
                  {(() => {
                    const x = parseFloat(fileInfo.dimensions.x);
                    const y = parseFloat(fileInfo.dimensions.y);
                    const z = parseFloat(fileInfo.dimensions.z);
                    return `${(x / 2.54).toFixed(2)} × ${(y / 2.54).toFixed(2)} × ${(z / 2.54).toFixed(2)} in`;
                  })()}
                </span>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ThreeDPreview;
